import React from 'react';
import { useIntl } from 'react-intl-next';

import type { RendererAppearance } from '@atlaskit/renderer';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { CONTENT_RENDERER_METRIC } from '@confluence/view-content-perf-metrics/entry-points/perf.config.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { useIsAttachmentDownloadDisabled } from '@confluence/data-security/entry-points/useIsAttachmentDownloadDisabled.ts';
import { updateLegacyImageUrls } from '@confluence/data-security/entry-points/updateLegacyImageUrls.ts';

import type { ContentBodyType } from '../__types__/index.ts';
import { TinyMCEServerFullRenderer } from '../TinyMCEServerFullRenderer.tsx';
import { TinyMCEClientRenderer } from '../TinyMCEClientRenderer.tsx';

import { StyledRenderer } from './StyledRenderer.tsx';

type PageContentRenderTinyProps = {
	contentAppearance: RendererAppearance;
	contentBody: ContentBodyType;
	contentId: string;
	spaceKey?: string | null;
	getLegacyContentExtensions?: () => any;
	markerRefs: string[];
	queryHash?: string;
	renderPerformanceMetrics: (props) => (...args: any) => JSX.Element;
	revision: string;
	setRootEl: (el: HTMLDivElement | null) => void;
	lastModifiedDate?: string | null;
};
const PageContentRendererTinyInternal = ({
	contentAppearance,
	contentBody,
	contentId,
	spaceKey,
	getLegacyContentExtensions,
	markerRefs,
	queryHash,
	renderPerformanceMetrics,
	revision,
	setRootEl,
	lastModifiedDate,
}: PageContentRenderTinyProps) => {
	const intl = useIntl();
	const { cloudId } = useSessionData();

	const tinyBodyRaw = contentBody?.value || '';
	const webresource = contentBody?.webresource;
	const css = webresource?.tags?.css || '';
	const js = webresource?.tags?.js || '';
	const superbatchJs = webresource?.superbatch?.tags?.js || '';
	const superbatchCss = webresource?.superbatch?.tags?.css || '';

	// Will always be true when skipped (cc_dlp-legacy-dl-block-attachments FG is off),
	// necessitating additional FG check below
	const { isDisabled: isAttachmentDownloadDisabled } = useIsAttachmentDownloadDisabled({
		contentId,
		spaceKey,
		skip: !fg('cc_dlp-legacy-dl-block-attachments'),
	});

	const tinyBody =
		isAttachmentDownloadDisabled && fg('cc_dlp-legacy-dl-block-attachments')
			? updateLegacyImageUrls(tinyBodyRaw)
			: tinyBodyRaw;

	return (
		<>
			<PageSegmentLoadStart key={contentId} metric={CONTENT_RENDERER_METRIC} />
			<StyledRenderer
				ref={setRootEl}
				data-test-appearance={contentAppearance}
				markerRefs={markerRefs}
				data-testid="pageContentRendererTinyRendererTestId"
				intl={intl}
			>
				{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
				{process.env.REACT_SSR ? (
					<TinyMCEServerFullRenderer
						body={tinyBody}
						superbatchCss={superbatchCss}
						superbatchJs={superbatchJs}
						css={css}
						js={js}
						contentId={contentId}
					/>
				) : (
					<TinyMCEClientRenderer
						body={tinyBody}
						scrollTo={queryHash || ''}
						isAnonymous={false}
						jsDependencies={js}
						cssDependencies={css}
						renderWhenReady={renderPerformanceMetrics({
							contentId,
							isFabric: false,
							revision,
							lastModifiedDate,
						})}
						getLegacyContentExtensions={getLegacyContentExtensions}
						cloudId={cloudId}
						contentId={contentId}
					/>
				)}
			</StyledRenderer>
		</>
	);
};

export const PageContentRendererTiny: typeof PageContentRendererTinyInternal = (props) => {
	return (
		<UFOSegment name="page-content-renderer-tiny">
			<PageContentRendererTinyInternal {...props} />
		</UFOSegment>
	);
};
